import React, { useState, useEffect } from 'react';
import { FlaskConical, RefreshCw } from 'lucide-react';
import { api } from '../api';

const statusConfig = {
  pending:  { cls: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  approved: { cls: 'bg-green-100 text-green-800',  label: 'Approved' },
  rejected: { cls: 'bg-red-100 text-red-800',      label: 'Rejected' },
};

const MyLabRequestsPage = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await api.request('/lab-requests');
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const statusBadge = (s) => {
    const cfg = statusConfig[s] || { cls: 'bg-gray-100 text-gray-700', label: s };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${cfg.cls}`}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div className="space-y-4" data-testid="my-lab-requests-page">
      <div className="bg-levelup-dark rounded-xl p-4 text-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-3">
            <FlaskConical size={26} className="text-levelup-accent" />
            My Lab Requests
          </h1>
          <p className="text-gray-300 mt-1 text-sm">
            {user?.role === 'partner_admin'
              ? 'All lab requests raised by your organisation'
              : 'Lab provisioning requests you have submitted for your workshops'}
          </p>
        </div>
        <button
          onClick={fetchRequests}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-colors"
          data-testid="refresh-lab-requests"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto" />
          <p className="text-gray-400 mt-3 text-sm">Loading requests…</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <FlaskConical size={44} className="text-gray-200 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No lab requests yet</p>
          <p className="text-gray-400 text-sm mt-1">
            When you schedule a workshop with a lab activity and request lab provisioning, it will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  'Course',
                  ...(user?.role === 'partner_admin' ? ['Trainer'] : []),
                  'Workshop Date',
                  'Location',
                  'Expected Attendees',
                  'Lab Attendees',
                  'Status',
                  'Admin Notes',
                  'Submitted',
                ].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map(req => (
                <tr key={req.id} className="hover:bg-gray-50 transition-colors" data-testid={`lab-req-row-${req.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900 max-w-[200px]">
                    <span className="line-clamp-2" title={req.course_title}>{req.course_title}</span>
                  </td>
                  {user?.role === 'partner_admin' && (
                    <td className="px-3 py-2 text-gray-600">
                      <div className="text-sm">{req.requester_name}</div>
                      <div className="text-xs text-gray-400">{req.requester_email}</div>
                    </td>
                  )}
                  <td className="px-3 py-2 text-gray-600">
                    {req.workshop_date ? req.workshop_date.split('T')[0] : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{req.location || '—'}</td>
                  <td className="px-3 py-2 text-center text-gray-700">{req.expected_attendees}</td>
                  <td className="px-3 py-2 text-center font-semibold text-blue-700">{req.expected_lab_attendees}</td>
                  <td className="px-3 py-2">{statusBadge(req.status)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs max-w-[160px]">
                    {req.admin_notes || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">
                    {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
            {requests.length} request{requests.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLabRequestsPage;
